import { LibelleLogement } from './../../../constants/libelle-logement';
import { FormGroup, ControlContainer, Validators, ValidatorFn, ValidationErrors } from '@angular/forms';
import { Component, OnInit, OnDestroy, Output, EventEmitter, Input, AfterContentChecked, ChangeDetectorRef } from '@angular/core';
import { Subscription } from 'rxjs';
import { InfoLongementForm, LogementModel } from './models/logement.model';
import { ConstantePage } from './../../../constants/constante-page';
import { ConstantesEligibilite } from './../../../constants/constante-eligibilite';
import { TagWaService } from '../../../services/tag-wa.service';
import { CcdStringUtils } from '../../../services/ccd-string.utils';
import { CcdIntervalleModel, CcdLocaliteModel } from '@cnaf.usagers.appli/cnaf-commons-dral';
import { HttpErrorResponse } from '@angular/common/http';
import { Router } from '@angular/router';
import { EnfantService } from '../../../02-declaration-enfant/components/enfant/services/enfant.service';


@Component({
  selector: 'app-info-logement',
  templateUrl: './info-logement.component.html'
})
export class InfoLogementComponent implements OnInit, OnDestroy, AfterContentChecked {

  @Input() logementModel: LogementModel;
  
  @Input() name : string;

  // renvoie le bean LogementModel
  @Output() logementModelEvent = new EventEmitter<LogementModel>();

  localite : CcdLocaliteModel = new CcdLocaliteModel();  
  lieuResidenceValue = "";
  typeOccupationValue = "";
  typeCrousValue = "";
  estMeubleValue = "";
  mttDeclareValue = "";
  estConventionneValue = "";

  isMetropole = true;
  afficheBlocLieuResidence = false;
  parentForm : FormGroup;
  logementForm : FormGroup<InfoLongementForm>;

  // Tableaux de constantes pour gérer les questions sur le logement
  libelleLogement = LibelleLogement;

  // Subscribers
  subscribers : Array<Subscription> = new Array();
  
  declencherModaleNonEligible = false;

  listeExclusion : Array<CcdIntervalleModel>;

  validatorsMontantLoyer = [Validators.required, Validators.pattern("^([1-9][0-9]*)$"), this.validationSup3000()];
  mapErreursMontantLoyer : Map<string, string> = new Map(
    [
      ["pattern", "Ce montant doit être un nombre entier"],
      ["required", "Champ obligatoire"],
      ["errSup3000", "Ce montant ne peut pas être supérieur à 3000"]
    ]
  );

  constructor(
    private readonly controlContainer: ControlContainer,
    private readonly router: Router,
    private readonly tagWaService: TagWaService,
    private readonly cd: ChangeDetectorRef,
    private readonly enfantService: EnfantService
  ) {}

  ngOnInit(): void {
    this.logementForm = new FormGroup<InfoLongementForm>({});
    this.listeExclusion = ConstantesEligibilite.INTERVALLES_CODES_POSTAUX_NON_ELIGIBLES;
    this.parentForm = this.controlContainer?.control as FormGroup;
    this.parentForm.addControl(this.name, this.logementForm);
    if (this.logementModel.localite.codePostal !== '') {
      this.mapSessionData();
      this.afficheBlocLieuResidence = true;
    } 
    
    this.subscribers.push(this.logementForm.valueChanges.subscribe(dataChange => {
      this.bindingLogementModel();
      this.isMetropole = this.enfantService.estMetropole( Number(this.localite?.codePostal) );
    }));
  }

  ngAfterContentChecked() : void {
    this.cd.detectChanges();
  }
  
  gestionFermetureModale() : void {
    this.localite = new CcdLocaliteModel();
    this.declencherModaleNonEligible = false;
  }

  mapSessionData() : void {    
    this.localite = this.logementModel.localite;
    this.lieuResidenceValue = this.logementModel.lieuResidence;
    this.typeOccupationValue = this.logementModel.typeOccupation;
    this.typeCrousValue = this.logementModel.typeCrous;
    this.estMeubleValue = this.logementModel.estMeuble;
    this.estConventionneValue = this.logementModel.estConventionne;
    this.mttDeclareValue = CcdStringUtils.numberToString(this.logementModel.mttDeclare);

    this.logementForm.markAsDirty();
  }

  ngOnDestroy(): void {
    this.subscribers.forEach(sub => sub.unsubscribe());

    this.parentForm.removeControl(this.name);
  }

  bindingLogementModel() {
    this.logementModel = new LogementModel(
      this.localite,
      this.lieuResidenceValue, // = cdTypeLogement
      this.typeOccupationValue,
      this.typeCrousValue,
      this.estMeubleValue,
      CcdStringUtils.stringToNumber(this.mttDeclareValue),
      this.estConventionneValue
    );
    this.logementModelEvent.emit(this.logementModel);
  }

  /**
   * Détermine le libellé du montant (loyer / redevance ...)
   */
  getLibelleMontant() : string {
    let libelle = "";
      if (this.lieuResidenceValue === 'LOGEMENT_CROUS') {
      libelle = "Montant mensuel de votre loyer";

    } else if (['APPARTEMENT_OU_MAISON', 'CHAMBRE'].includes(this.lieuResidenceValue)) {
        if (this.typeOccupationValue === 'LOCATION') {
        libelle = "Montant mensuel de votre loyer "
      } else {
        libelle = "Part de votre loyer mensuel ";
      }
        if (this.estMeubleValue === 'true') {
        libelle = libelle.concat("(charges comprises)");
      } else {
        libelle = libelle.concat("(hors charges)");
      }
    }
    else {
      libelle = "Montant mensuel de votre redevance";
    }
    return libelle;
  }

  /**
   * Methode pour taguer un clic
   * 
   * Tag de la popup Nature de logement
   */
  clickPopup() : void {
    // On tague la popup Nature de logement pour le WA
    this.tagWaService.tagClic(ConstantePage.STATS_CLIC_NATURE_LOGEMENT, ConstantePage.STATS_PAGE_DECLARATION);   
  }

  /**
   * Redirection vers la page d'erreur technique qi erreur HTTP du composant code postal
   * @param $err 
   */
  gerrerErreursCodePostal($err : HttpErrorResponse) {
    this.router.navigateByUrl('/page-erreur-technique');
  }

  /**
   * Validator qui envoie une erreur si le montant du loyer est supérieur à 3000
   */
  validationSup3000(): ValidatorFn {
    return (controls: FormGroup): ValidationErrors | null => {
      const montant = CcdStringUtils.stringToNumber(this.mttDeclareValue);
      const isSup3000 = montant && (montant > 3000);
      let result = null;
      if (isSup3000) {
        result = {errSup3000 : true};
      }
      return result;
    };
  }
}
